/**
 * New variant: pick a game and a release on the left, what to start from on
 * the right — the game's defaults, or one or many of the variants the release
 * ships.
 *
 * One starting point makes one variant, titled here and opened afterwards.
 * Several make a variant each, named after the game's own, in one go: a game
 * can ship a hundred, and that is not a hundred dialogs. Game variants this
 * company already made a variant from are marked, and "New only" ticks the
 * rest, so after a new release the same dialog brings only what is new.
 *
 * Only the games put on this base are listed (pe3d/catalog/games; the Runtime
 * bases screen, selectorPe3dGames.js, changes that list). Many at once run as a job on the
 * server (pe3d/variant/importGameVariants) with the same start and the same
 * checks as one. Laid out like the other pe3d dialogs (pe3dPanelKit).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Radio,
  TextField,
  Typography,
} from "@material-ui/core";
import { CONTROL_BG, CONTROL_BORDER, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import { fetchReleaseFile, listOpenGames } from "../../modules/pe3dApi";
import { LANGUAGE_CAPTIONS } from "../../modules/pe3dLocalization";
import { DIALOG_CAPTION, DIALOG_CAPTION_NOTE, DIALOG_PAPER_PROPS, DIALOG_TEXT, MUTED, ROW_HOVER, TEXT } from "./pe3dPanelKit";

const PAPER_PROPS = { style: { ...DIALOG_PAPER_PROPS.style, width: 780, maxWidth: "calc(100vw - 64px)" } };
const COLUMNS = { display: "grid", gridTemplateColumns: "232px minmax(0, 1fr)", columnGap: 20, alignItems: "start" };
// A column's caption: the small capitals of a settings section, without the rule.
const BOX = { backgroundColor: CONTROL_BG, border: `1px solid ${CONTROL_BORDER}`, borderRadius: 4, overflow: "hidden" };
const GAME_TITLE = { fontSize: 13, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
// The menu card of Version Settings' language selector, which the game menu follows.
const CARD_BG = "#474747";
const MENU_SEARCH = { padding: "4px 8px", borderBottom: `1px solid ${SECTION_DIVIDER}` };
const MENU_EMPTY = { padding: "8px 10px", fontSize: 12, color: MUTED };
// The "tick them all" row of the language menu; never a language itself.
const ALL_LANGUAGES_VALUE = "__all__";
const SMALL = { fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const SHOT = { ...BOX, marginTop: 12, height: 150, display: "flex", alignItems: "center", justifyContent: "center" };
const FACTS = { marginTop: 8, fontSize: 11, lineHeight: 1.7, color: MUTED };
const TOOLBAR = { display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderBottom: `1px solid ${SECTION_DIVIDER}` };
const LINK = { fontSize: 11, minWidth: 0, padding: "0 4px", color: "#4eabb1", textTransform: "none" };
const START_LIST = { height: 200, overflowY: "auto" };
const START_ROW = { display: "flex", alignItems: "center", gap: 2, paddingRight: 10, cursor: "pointer" };
const START_NAME = { flex: 1, minWidth: 0, fontSize: 13, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const FIELDS = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 190px", columnGap: 12, marginTop: 4 };
const FAILURES = { ...BOX, marginTop: 6, maxHeight: 120, overflowY: "auto", padding: "6px 10px", fontSize: 12, color: "#ef9a9a" };

const errorOf = (result) => {
  const error = result && result.error;
  if (!error) return `HTTP ${result && result.status}`;
  return typeof error === "string" ? error : error.error || JSON.stringify(error);
};
const failureText = (item) => (Array.isArray(item.problems) && item.problems.length ? item.problems.join("; ") : item.detail || item.error || "refused");
const countOf = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const dayOf = (date) => new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** The release's screenshot to show: the preview network's portrait when there is one. */
function shotOf(release) {
  const shots = (release && release.screenshots) || [];
  return shots.find((file) => /default-portrait/.test(file)) || shots.find((file) => /portrait/.test(file)) || shots[0] || null;
}

/**
 * @param props.editor       usePe3dEditor (its base, variants, create and importGameVariants)
 * @param props.folderID     the folder the dialog was opened on; the top level without it
 * @param props.folderTitle  its name, for the hint
 */
export default function DialogPe3dNewVariant({ isOpen, editor, folderID, folderTitle, canGrantGames, onClose, createSnackbar }) {
  const [games, setGames] = useState(null);
  const [error, setError] = useState("");
  const [gamePackage, setGamePackage] = useState("");
  const [version, setVersion] = useState("");
  // The starting point: the game's defaults (`blank`), or the ticked game variants.
  const [blank, setBlank] = useState(false);
  const [ticked, setTicked] = useState([]);
  const [query, setQuery] = useState("");
  const [gameQuery, setGameQuery] = useState("");
  // Beyond "en", which every variant is; only a release with the feature takes them.
  const [languages, setLanguages] = useState([]);
  const [title, setTitle] = useState("");
  const [isTitleTyped, setIsTitleTyped] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState("");
  // What the last run of many could not make: [{ name, error, problems }]; the dialog stays open to show it.
  const [failed, setFailed] = useState(null);
  const [shotUrl, setShotUrl] = useState(null);
  const [hovered, setHovered] = useState(null);

  const baseID = editor.baseID;
  useEffect(() => {
    if (!isOpen) return;
    setGames(null);
    setError("");
    setQuery("");
    setGameQuery("");
    setLanguages([]);
    setIsRunning(false);
    setProgress("");
    setFailed(null);
    listOpenGames(baseID).then((result) => {
      if (!result.ok) {
        setError(`Could not load the games: ${errorOf(result)}`);
        setGames([]);
        return;
      }
      const list = (result.data.games || []).filter((game) => game.releases.length > 0);
      setGames(list);
      setGamePackage((current) => (list.some((game) => game.package === current) ? current : (list[0] && list[0].package) || ""));
    });
  }, [isOpen, baseID]);

  const game = useMemo(() => (games || []).find((item) => item.package === gamePackage) || null, [games, gamePackage]);
  const release = useMemo(() => (game ? game.releases.find((item) => item.version === version) : null) || null, [game, version]);
  const names = (release && release.variants) || [];

  // The game variants this company already has a variant of, on this base.
  const alreadyHere = useMemo(
    () => new Set((editor.variants || []).filter((item) => item.game === gamePackage && item.startedFrom).map((item) => item.startedFrom)),
    [editor.variants, gamePackage]
  );

  // A game opens on its newest release; a release on its "default" variant, or the game's defaults.
  useEffect(() => {
    if (!game) return;
    setVersion(game.releases[0].version);
    setFolderName("");
  }, [game && game.package]);
  useEffect(() => {
    if (!release) return;
    const hasDefault = names.includes("default");
    setBlank(!hasDefault);
    setTicked(hasDefault ? ["default"] : []);
    setIsTitleTyped(false);
    setFailed(null);
    setLanguages([]);
  }, [release && release.version, game && game.package, isOpen]);

  // A release without the localization feature makes one-language variants, as before.
  const localized = Boolean(release && release.localization);
  const otherLanguages = useMemo(() => Object.keys(LANGUAGE_CAPTIONS).filter((code) => code !== "en"), []);
  const isAllLanguages = localized && languages.length === otherLanguages.length;

  const isMany = !blank && ticked.length > 1;
  const single = blank ? null : ticked.length === 1 ? ticked[0] : undefined; // undefined = no single start

  // One start suggests its own name as the title until the user types one.
  useEffect(() => {
    if (!isTitleTyped) setTitle(single || "");
  }, [single, blank, isTitleTyped]);

  // The release's screenshot, when it ships one.
  const shot = shotOf(release);
  useEffect(() => {
    if (!isOpen || !game || !release || !shot) {
      setShotUrl(null);
      return undefined;
    }
    let url = null;
    let isCurrent = true;
    fetchReleaseFile(game.package, release.version, shot, "bytes")
      .then((bytes) => {
        if (!isCurrent) return;
        url = URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
        setShotUrl(url);
      })
      .catch(() => isCurrent && setShotUrl(null));
    return () => {
      isCurrent = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [isOpen, game && game.package, release && release.version, shot]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? names.filter((name) => name.toLowerCase().includes(needle)) : names;
  }, [names, query]);

  const shownGames = useMemo(() => {
    const needle = gameQuery.trim().toLowerCase();
    if (!needle) return games || [];
    return (games || []).filter((item) => `${item.title} ${item.package}`.toLowerCase().includes(needle));
  }, [games, gameQuery]);

  const pickBlank = () => {
    setBlank(true);
    setTicked([]);
  };
  const tick = (next) => {
    setBlank(false);
    setTicked(next);
  };
  const toggle = (name) => tick(ticked.includes(name) ? ticked.filter((item) => item !== name) : [...ticked, name]);

  const createOne = async () => {
    setIsRunning(true);
    const created = await editor.create({ title: title.trim(), game: game.package, release: release.version, startFrom: blank ? null : ticked[0], folderID, languages: localized ? languages : [] });
    setIsRunning(false);
    if (created) onClose();
  };

  const createMany = async () => {
    setIsRunning(true);
    setError("");
    setFailed(null);
    setProgress("Starting…");
    const result = await editor.importGameVariants(
      { game: game.package, release: release.version, names: names.filter((name) => ticked.includes(name)), folderID, folderName: folderName.trim(), languages: localized ? languages : [] },
      {
        onProgress: (events) => {
          const last = events[events.length - 1] || {};
          setProgress(last.total ? `${last.done || 0} of ${last.total} checked` : "Working…");
        },
      }
    );
    setIsRunning(false);
    const body = result.ok ? result.data : result.error;
    const notMade = (body && Array.isArray(body.failed) && body.failed) || [];
    if (!result.ok && !notMade.length) {
      setError(`Could not make the variants: ${errorOf(result)}`);
      return;
    }
    const made = result.ok ? (result.data.created || []).length : 0;
    if (made && createSnackbar) {
      createSnackbar(`${countOf(made, "variant", "variants")} made${result.data.folderName ? ` in “${result.data.folderName}”` : ""}.`, "success", false);
    }
    if (notMade.length) {
      // What stays ticked is what did not make it, to be looked at or tried again.
      setFailed(notMade);
      tick(notMade.map((item) => item.name));
      return;
    }
    onClose();
  };

  const hasStart = blank || ticked.length > 0;
  const canCreate = !isRunning && game && release && hasStart && (isMany || title.trim());
  const where = folderTitle ? `“${folderTitle}”` : "the top level";

  return (
    <Dialog open={Boolean(isOpen)} onClose={isRunning ? undefined : onClose} maxWidth={false} PaperProps={PAPER_PROPS}>
      <DialogTitle>New variant</DialogTitle>
      <DialogContent>
        {games === null && (
          <div style={{ ...DIALOG_TEXT.busy, marginTop: 0 }}>
            <CircularProgress size={18} /> Loading the games…
          </div>
        )}
        {error && <Typography style={DIALOG_TEXT.failure}>{error}</Typography>}
        {games && games.length === 0 && !error && (
          <Typography style={{ ...DIALOG_TEXT.hint, marginTop: 0 }}>
            {canGrantGames
              ? "No game is on this base yet. Put one on it from Account settings → Runtime bases."
              : "No game has been put on this base yet. Ask Playin to add one."}
          </Typography>
        )}
        {games && games.length > 0 && (
          <div style={COLUMNS}>
            {/* ── The game ── */}
            <div>
              <div style={DIALOG_CAPTION}>Game</div>
              <TextField
                select
                style={{ width: "100%" }}
                size="small"
                value={gamePackage}
                disabled={isRunning}
                onChange={(event) => setGamePackage(event.target.value)}
                SelectProps={{
                  renderValue: () => (game ? game.title : ""),
                  MenuProps: { PaperProps: { style: { backgroundColor: CARD_BG, maxHeight: 420 } }, autoFocus: false },
                  onClose: () => setGameQuery(""),
                }}
              >
                {/* Typing filters the menu; the keys must not reach the select, which jumps to a matching option. */}
                <div style={MENU_SEARCH} onKeyDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
                  <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder="Search the games"
                    InputProps={{ disableUnderline: true, style: { fontSize: 13 } }}
                    value={gameQuery}
                    onChange={(event) => setGameQuery(event.target.value)}
                  />
                </div>
                {shownGames.length === 0 && <div style={MENU_EMPTY}>No game matches.</div>}
                {shownGames.map((item) => (
                  <MenuItem key={item.package} value={item.package}>
                    <div style={{ minWidth: 0 }}>
                      <div style={GAME_TITLE}>{item.title}</div>
                      <div style={SMALL}>
                        {countOf(item.releases.length, "release", "releases")} · newest {item.releases[0].version}
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </TextField>
              {game && (
                <TextField select style={{ width: "100%", marginTop: 14 }} size="small" label="Release" value={version} disabled={isRunning} onChange={(event) => setVersion(event.target.value)}>
                  {game.releases.map((item, index) => (
                    <MenuItem key={item.version} value={item.version}>
                      {item.version}
                      {index === 0 ? " (newest)" : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {release && shotUrl && (
                <div style={SHOT}>
                  <img src={shotUrl} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </div>
              )}
              {release && (
                <div style={FACTS}>
                  <div>{countOf(names.length, "variant of its own", "variants of its own")}</div>
                  <div>
                    {countOf((release.networks || []).length, "network", "networks")}
                    {release.engineVersion ? ` · engine ${release.engineVersion}` : ""}
                  </div>
                  {release.builtAt && <div>built {dayOf(release.builtAt)}</div>}
                </div>
              )}
            </div>

            {/* ── What to start from ── */}
            <div style={{ minWidth: 0 }}>
              <div style={DIALOG_CAPTION}>
                <span>Start from</span>
                <span style={DIALOG_CAPTION_NOTE}>{blank ? "the game's defaults" : ticked.length ? `${ticked.length} of ${names.length} ticked` : "nothing ticked"}</span>
              </div>
              <div style={BOX}>
                {names.length > 0 && (
                  <div style={TOOLBAR}>
                    <TextField
                      style={{ flex: 1 }}
                      size="small"
                      placeholder="Search the game's variants"
                      InputProps={{ disableUnderline: true, style: { fontSize: 13 } }}
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                    />
                    <Button style={LINK} disabled={isRunning} onClick={() => tick(names)}>
                      All
                    </Button>
                    {alreadyHere.size > 0 && (
                      <Button style={LINK} disabled={isRunning} onClick={() => tick(names.filter((name) => !alreadyHere.has(name)))}>
                        New only
                      </Button>
                    )}
                    <Button style={LINK} disabled={isRunning} onClick={() => tick([])}>
                      None
                    </Button>
                  </div>
                )}
                <div style={START_LIST} className="proto-dark-scroll">
                  {!query.trim() && (
                    <div
                      style={{ ...START_ROW, borderBottom: `1px solid ${SECTION_DIVIDER}`, ...(hovered === "blank" ? { backgroundColor: ROW_HOVER } : null) }}
                      onMouseEnter={() => setHovered("blank")}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => !isRunning && pickBlank()}
                    >
                      <Radio size="small" color="primary" checked={blank} disabled={isRunning} />
                      <span style={START_NAME}>The game&apos;s defaults</span>
                      <span style={SMALL}>one variant, nothing changed</span>
                    </div>
                  )}
                  {names.length === 0 && <div style={{ padding: "12px 10px", fontSize: 12, color: MUTED }}>This release ships no variants of its own.</div>}
                  {names.length > 0 && shown.length === 0 && <div style={{ padding: "12px 10px", fontSize: 12, color: MUTED }}>No variant matches.</div>}
                  {shown.map((name) => (
                    <div
                      key={name}
                      style={{ ...START_ROW, ...(hovered === name ? { backgroundColor: ROW_HOVER } : null) }}
                      onMouseEnter={() => setHovered(name)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => !isRunning && toggle(name)}
                    >
                      <Checkbox size="small" color="primary" checked={!blank && ticked.includes(name)} disabled={isRunning} />
                      <span style={START_NAME}>{name}</span>
                      {alreadyHere.has(name) && <span style={SMALL}>already here</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div style={FIELDS}>
                {isMany ? (
                  <TextField
                    style={DIALOG_TEXT.field}
                    size="small"
                    label="In a new folder"
                    placeholder="No folder of their own"
                    value={folderName}
                    disabled={isRunning}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: 120 }}
                    onChange={(event) => setFolderName(event.target.value)}
                  />
                ) : (
                  <TextField
                    style={DIALOG_TEXT.field}
                    size="small"
                    label="Title"
                    value={title}
                    disabled={isRunning || !hasStart}
                    autoFocus
                    inputProps={{ maxLength: 120 }}
                    onChange={(event) => {
                      setTitle(event.target.value);
                      setIsTitleTyped(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && canCreate) createOne();
                    }}
                  />
                )}
                {localized && (
                  <TextField
                    select
                    style={DIALOG_TEXT.field}
                    size="small"
                    label="Languages"
                    value={languages}
                    disabled={isRunning}
                    SelectProps={{
                      multiple: true,
                      renderValue: () => (isAllLanguages ? "All languages" : languages.length ? `English +${languages.length}` : "English"),
                      MenuProps: { PaperProps: { style: { backgroundColor: CARD_BG, maxHeight: 420 } } },
                    }}
                    onChange={(event) => setLanguages(event.target.value.filter((code) => code !== ALL_LANGUAGES_VALUE))}
                  >
                    <MenuItem
                      value={ALL_LANGUAGES_VALUE}
                      onClick={() => !isRunning && setLanguages(isAllLanguages ? [] : otherLanguages)}
                      style={{ fontSize: 13 }}
                    >
                      <Checkbox size="small" color="primary" checked={isAllLanguages} />
                      All languages
                    </MenuItem>
                    {otherLanguages.map((code) => (
                      <MenuItem key={code} value={code} style={{ fontSize: 13 }}>
                        <Checkbox size="small" color="primary" checked={languages.includes(code)} />
                        {LANGUAGE_CAPTIONS[code]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </div>
              <Typography style={DIALOG_TEXT.hint}>
                {isMany
                  ? `Each ticked one becomes a variant named after it, in ${folderName.trim() ? `“${folderName.trim()}” inside ${where}` : where}.`
                  : `The variant is made in ${where}.`}
                {localized && ` It carries English${languages.length ? ` and ${countOf(languages.length, "more language", "more languages")}` : " only"}; more can be added later.`}
              </Typography>

              {isRunning && isMany && (
                <div style={DIALOG_TEXT.busy}>
                  <CircularProgress size={18} />
                  <span>
                    Making the variants…
                    {progress && <span style={DIALOG_TEXT.waitLine}>{progress}</span>}
                  </span>
                </div>
              )}
              {failed && failed.length > 0 && (
                <>
                  <Typography style={DIALOG_TEXT.failure}>
                    {failed.length} could not be made; the release refused {failed.length === 1 ? "it" : "them"}:
                  </Typography>
                  <div style={FAILURES} className="proto-dark-scroll">
                    {failed.map((item) => (
                      <div key={item.name}>
                        <b>{item.name}</b>: {failureText(item)}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isRunning}>
          {failed ? "Close" : "Cancel"}
        </Button>
        <Button color="primary" variant="contained" disabled={!canCreate} onClick={isMany ? createMany : createOne}>
          {isRunning ? <CircularProgress size={16} color="inherit" /> : isMany ? `Create ${ticked.length} variants` : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
